import React, { useState, useEffect, useRef } from "react";
import { theme } from "../../styles/theme";

// Exchange rates: 1 GBP = X (approximate)
const GBP_TO_USD_RATE = 1.27;
const USD_TO_GBP_RATE = 1 / GBP_TO_USD_RATE;
const GBP_TO_EUR_RATE = 1.17;
const EUR_TO_GBP_RATE = 1 / GBP_TO_EUR_RATE;
const USD_TO_EUR_RATE = GBP_TO_EUR_RATE / GBP_TO_USD_RATE;
const EUR_TO_USD_RATE = 1 / USD_TO_EUR_RATE;

interface CurrencyInputProps {
  label: string;
  valueGbpPence: number;
  valueUsdCents: number;
  onChangeGbp: (pence: number) => void;
  onChangeUsd: (cents: number) => void;
  showBothCurrencies?: boolean;
  /** Optional EUR pair. When provided, an EUR input renders inline and
   *  auto-converts from GBP at GBP_TO_EUR_RATE. */
  valueEurCents?: number;
  onChangeEur?: (cents: number) => void;
}

/** Dual-currency price input that syncs GBP and USD values via exchange rate. */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  label,
  valueGbpPence,
  valueUsdCents,
  onChangeGbp,
  onChangeUsd,
  showBothCurrencies = true,
  valueEurCents,
  onChangeEur,
}) => {
  const showEur =
    typeof valueEurCents === "number" && typeof onChangeEur === "function";
  const eurValue = valueEurCents ?? 0;
  const [gbpDisplay, setGbpDisplay] = useState(
    (valueGbpPence / 100).toFixed(2)
  );
  const [usdDisplay, setUsdDisplay] = useState(
    (valueUsdCents / 100).toFixed(2)
  );
  const [eurDisplay, setEurDisplay] = useState((eurValue / 100).toFixed(2));
  const [gbpFocused, setGbpFocused] = useState(false);
  const [usdFocused, setUsdFocused] = useState(false);
  const [eurFocused, setEurFocused] = useState(false);
  // Independent link toggles per adjacent pair: GBP↔USD and USD↔EUR.
  // Off = inputs are edited independently; on = typing in either side
  // syncs the other via the exchange rate. Link transitivity: if both
  // toggles are on, editing GBP propagates GBP→USD→EUR (and vice versa).
  const [linkGbpUsd, setLinkGbpUsd] = useState(true);
  const [linkUsdEur, setLinkUsdEur] = useState(true);
  const prevGbpRef = useRef(valueGbpPence);
  const prevUsdRef = useRef(valueUsdCents);
  const prevEurRef = useRef(eurValue);

  // Update display when external values change (only if not focused and value actually changed externally)
  useEffect(() => {
    if (!gbpFocused && valueGbpPence !== prevGbpRef.current) {
      setGbpDisplay((valueGbpPence / 100).toFixed(2));
    }
    prevGbpRef.current = valueGbpPence;
  }, [valueGbpPence, gbpFocused]);

  useEffect(() => {
    if (!usdFocused && valueUsdCents !== prevUsdRef.current) {
      setUsdDisplay((valueUsdCents / 100).toFixed(2));
    }
    prevUsdRef.current = valueUsdCents;
  }, [valueUsdCents, usdFocused]);

  useEffect(() => {
    if (!eurFocused && eurValue !== prevEurRef.current) {
      setEurDisplay((eurValue / 100).toFixed(2));
    }
    prevEurRef.current = eurValue;
  }, [eurValue, eurFocused]);

  const handleGbpChange = (value: string) => {
    setGbpDisplay(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const pence = Math.round(numValue * 100);
      onChangeGbp(pence);

      if (showBothCurrencies && linkGbpUsd) {
        const usdCents = Math.round(pence * GBP_TO_USD_RATE);
        onChangeUsd(usdCents);
        setUsdDisplay((usdCents / 100).toFixed(2));

        // Propagate GBP → EUR transitively only if both pairs are linked.
        if (showEur && onChangeEur && linkUsdEur) {
          const eurCents = Math.round(usdCents * USD_TO_EUR_RATE);
          onChangeEur(eurCents);
          setEurDisplay((eurCents / 100).toFixed(2));
        }
      }
    }
  };

  const handleUsdChange = (value: string) => {
    setUsdDisplay(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const cents = Math.round(numValue * 100);
      onChangeUsd(cents);

      if (showBothCurrencies && linkGbpUsd) {
        const gbpPence = Math.round(cents * USD_TO_GBP_RATE);
        onChangeGbp(gbpPence);
        setGbpDisplay((gbpPence / 100).toFixed(2));
      }

      if (showEur && onChangeEur && linkUsdEur) {
        const eurCents = Math.round(cents * USD_TO_EUR_RATE);
        onChangeEur(eurCents);
        setEurDisplay((eurCents / 100).toFixed(2));
      }
    }
  };

  const handleEurChange = (value: string) => {
    setEurDisplay(value);
    if (!onChangeEur) return;

    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const cents = Math.round(numValue * 100);
      onChangeEur(cents);

      if (linkUsdEur) {
        const usdCents = Math.round(cents * EUR_TO_USD_RATE);
        onChangeUsd(usdCents);
        setUsdDisplay((usdCents / 100).toFixed(2));

        // Propagate EUR → GBP transitively only if both pairs are linked.
        if (showBothCurrencies && linkGbpUsd) {
          const gbpPence = Math.round(usdCents * USD_TO_GBP_RATE);
          onChangeGbp(gbpPence);
          setGbpDisplay((gbpPence / 100).toFixed(2));
        }
      }
    }
  };

  const handleGbpBlur = () => {
    setGbpFocused(false);
    const numValue = parseFloat(gbpDisplay);
    if (!isNaN(numValue)) {
      setGbpDisplay(numValue.toFixed(2));
    } else {
      setGbpDisplay("0.00");
      onChangeGbp(0);
    }
  };

  const handleUsdBlur = () => {
    setUsdFocused(false);
    const numValue = parseFloat(usdDisplay);
    if (!isNaN(numValue)) {
      setUsdDisplay(numValue.toFixed(2));
    } else {
      setUsdDisplay("0.00");
      onChangeUsd(0);
    }
  };

  const handleEurBlur = () => {
    setEurFocused(false);
    if (!onChangeEur) return;
    const numValue = parseFloat(eurDisplay);
    if (!isNaN(numValue)) {
      setEurDisplay(numValue.toFixed(2));
    } else {
      setEurDisplay("0.00");
      onChangeEur(0);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: `${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.sm} 32px`,
    background: theme.colors.bgTertiary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    color: theme.colors.textPrimary,
    fontSize: theme.fontSizes.sm,
    outline: "none",
  };

  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: theme.fontSizes.sm,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.xs,
        }}
      >
        {label}
      </label>
      <div
        style={{ display: "flex", gap: theme.spacing.sm, alignItems: "center" }}
      >
        {/* GBP Input */}
        <div style={{ flex: 1, position: "relative" }}>
          <span
            style={{
              position: "absolute",
              left: theme.spacing.sm,
              top: "50%",
              transform: "translateY(-50%)",
              color: theme.colors.textMuted,
              fontSize: theme.fontSizes.sm,
              fontWeight: 600,
            }}
          >
            £
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={gbpDisplay}
            onChange={(e) => handleGbpChange(e.target.value)}
            onBlur={handleGbpBlur}
            onFocus={(e) => {
              setGbpFocused(true);
              e.target.select();
            }}
            style={inputStyle}
            placeholder="0.00"
          />
          <span
            style={{
              position: "absolute",
              right: theme.spacing.sm,
              top: "50%",
              transform: "translateY(-50%)",
              color: theme.colors.textMuted,
              fontSize: theme.fontSizes.xs,
            }}
          >
            GBP
          </span>
        </div>

        {/* Link/Unlink Toggle: GBP ↔ USD */}
        {showBothCurrencies && (
          <button
            type="button"
            onClick={() => setLinkGbpUsd(!linkGbpUsd)}
            title={
              linkGbpUsd
                ? "Click to edit GBP and USD independently"
                : "Click to auto-convert GBP ↔ USD"
            }
            style={{
              background: linkGbpUsd
                ? theme.colors.primary + "20"
                : theme.colors.bgTertiary,
              border: `1px solid ${
                linkGbpUsd ? theme.colors.primary : theme.colors.border
              }`,
              borderRadius: theme.borderRadius.md,
              padding: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: linkGbpUsd ? theme.colors.primary : theme.colors.textMuted,
              transition: "all 0.2s ease",
            }}
          >
            {linkGbpUsd ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18.84 12.25l1.72-1.71a5 5 0 0 0-7.07-7.07l-3 3a5 5 0 0 0 .54 7.54" />
                <path d="M5.16 11.75l-1.72 1.71a5 5 0 0 0 7.07 7.07l3-3a5 5 0 0 0-.54-7.54" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            )}
          </button>
        )}

        {/* USD Input */}
        {showBothCurrencies && (
          <div style={{ flex: 1, position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: theme.spacing.sm,
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.colors.textMuted,
                fontSize: theme.fontSizes.sm,
                fontWeight: 600,
              }}
            >
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={usdDisplay}
              onChange={(e) => handleUsdChange(e.target.value)}
              onBlur={handleUsdBlur}
              onFocus={(e) => {
                setUsdFocused(true);
                e.target.select();
              }}
              style={inputStyle}
              placeholder="0.00"
            />
            <span
              style={{
                position: "absolute",
                right: theme.spacing.sm,
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.colors.textMuted,
                fontSize: theme.fontSizes.xs,
              }}
            >
              USD
            </span>
          </div>
        )}

        {/* Link/Unlink Toggle: USD ↔ EUR */}
        {showEur && (
          <button
            type="button"
            onClick={() => setLinkUsdEur(!linkUsdEur)}
            title={
              linkUsdEur
                ? "Click to edit USD and EUR independently"
                : "Click to auto-convert USD ↔ EUR"
            }
            style={{
              background: linkUsdEur
                ? theme.colors.primary + "20"
                : theme.colors.bgTertiary,
              border: `1px solid ${
                linkUsdEur ? theme.colors.primary : theme.colors.border
              }`,
              borderRadius: theme.borderRadius.md,
              padding: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: linkUsdEur ? theme.colors.primary : theme.colors.textMuted,
              transition: "all 0.2s ease",
            }}
          >
            {linkUsdEur ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18.84 12.25l1.72-1.71a5 5 0 0 0-7.07-7.07l-3 3a5 5 0 0 0 .54 7.54" />
                <path d="M5.16 11.75l-1.72 1.71a5 5 0 0 0 7.07 7.07l3-3a5 5 0 0 0-.54-7.54" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            )}
          </button>
        )}

        {/* EUR Input */}
        {showEur && (
          <div style={{ flex: 1, position: "relative" }}>
            <span
              style={{
                position: "absolute",
                left: theme.spacing.sm,
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.colors.textMuted,
                fontSize: theme.fontSizes.sm,
                fontWeight: 600,
              }}
            >
              €
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={eurDisplay}
              onChange={(e) => handleEurChange(e.target.value)}
              onBlur={handleEurBlur}
              onFocus={(e) => {
                setEurFocused(true);
                e.target.select();
              }}
              style={inputStyle}
              placeholder="0.00"
            />
            <span
              style={{
                position: "absolute",
                right: theme.spacing.sm,
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.colors.textMuted,
                fontSize: theme.fontSizes.xs,
              }}
            >
              EUR
            </span>
          </div>
        )}
      </div>
      {showBothCurrencies && (
        <p
          style={{
            fontSize: theme.fontSizes.xs,
            color:
              linkGbpUsd && (!showEur || linkUsdEur)
                ? theme.colors.textMuted
                : theme.colors.primary,
            marginTop: "4px",
          }}
        >
          {linkGbpUsd && (!showEur || linkUsdEur)
            ? showEur
              ? `Auto-converts at £1 = $${GBP_TO_USD_RATE.toFixed(
                  2
                )} = €${GBP_TO_EUR_RATE.toFixed(2)}`
              : `Auto-converts at £1 = $${GBP_TO_USD_RATE.toFixed(2)}`
            : "Some currencies unlinked - edit those independently"}
        </p>
      )}
    </div>
  );
};

// Simple number input with formatting
interface NumberInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
}

/** Numeric input with optional suffix, min/max, and step controls. */
export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  placeholder = "0",
  suffix,
  min,
  max,
  step = 1,
}) => {
  const [displayValue, setDisplayValue] = useState(value.toLocaleString());

  useEffect(() => {
    setDisplayValue(value.toLocaleString());
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/,/g, "");
    setDisplayValue(e.target.value);

    const numValue = parseInt(raw);
    if (!isNaN(numValue)) {
      let bounded = numValue;
      if (min !== undefined) bounded = Math.max(min, bounded);
      if (max !== undefined) bounded = Math.min(max, bounded);
      onChange(bounded);
    }
  };

  const handleBlur = () => {
    setDisplayValue(value.toLocaleString());
  };

  const increment = () => {
    let newValue = value + step;
    if (max !== undefined) newValue = Math.min(max, newValue);
    onChange(newValue);
  };

  const decrement = () => {
    let newValue = value - step;
    if (min !== undefined) newValue = Math.max(min, newValue);
    onChange(newValue);
  };

  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: theme.fontSizes.sm,
          color: theme.colors.textSecondary,
          marginBottom: theme.spacing.xs,
        }}
      >
        {label}
      </label>
      <div style={{ display: "flex", alignItems: "stretch" }}>
        <button
          type="button"
          onClick={decrement}
          style={{
            padding: `0 ${theme.spacing.sm}`,
            background: theme.colors.bgTertiary,
            border: `1px solid ${theme.colors.border}`,
            borderRight: "none",
            borderRadius: `${theme.borderRadius.md} 0 0 ${theme.borderRadius.md}`,
            color: theme.colors.textSecondary,
            cursor: "pointer",
            fontSize: theme.fontSizes.lg,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
          }}
        >
          −
        </button>
        <div style={{ position: "relative", flex: 1 }}>
          <input
            type="text"
            inputMode="numeric"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={(e) => e.target.select()}
            placeholder={placeholder}
            style={{
              width: "100%",
              padding: theme.spacing.sm,
              paddingRight: suffix ? "50px" : theme.spacing.sm,
              background: theme.colors.bgTertiary,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 0,
              color: theme.colors.textPrimary,
              fontSize: theme.fontSizes.sm,
              textAlign: "center",
              outline: "none",
            }}
          />
          {suffix && (
            <span
              style={{
                position: "absolute",
                right: theme.spacing.sm,
                top: "50%",
                transform: "translateY(-50%)",
                color: theme.colors.textMuted,
                fontSize: theme.fontSizes.xs,
              }}
            >
              {suffix}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={increment}
          style={{
            padding: `0 ${theme.spacing.sm}`,
            background: theme.colors.bgTertiary,
            border: `1px solid ${theme.colors.border}`,
            borderLeft: "none",
            borderRadius: `0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0`,
            color: theme.colors.textSecondary,
            cursor: "pointer",
            fontSize: theme.fontSizes.lg,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default CurrencyInput;
